import axios from 'axios';
import {
  HotjarFilters,
  HotjarFilterOptionAll,
  HotjarFilterOptionWatched,
  JsonFilters,
} from '../../../store/list/types';
import { getFiltersMergedWithRelevanceFilter } from '../../../utils/getFiltersMergedWithRelevanceFilter';
import { getSiteId } from '../../../utils/getParamsFromUrlPathname';
import { getUTCOffset } from '../utils/getUTCOffset';

/**
 * API Docs
 * https://insights-integration.live.eks.hotjar.com/autodocs/redoc/#tag/Recordings/operation/post__api_v1_sites_%7Bsite_id%7D_recordings_query-deletion
 */
type QueryDeletionPayload = {
  /* The global filters that determine which recordings to delete. */
  global_filters: string;

  /* The local timezone offset (from UTC), to aid in the resolution of temporal filters. */
  tz_offset: number;

  /* The "watched" local filter. Its absence indicates no-preference. This is the only local filter in Recordings. */
  watched?: boolean;
};

/**
 * Sends an API request that issues a Query Deletion for all recordings that match the given filters.
 *
 * @param globalFilters The global filters that determine which recordings to delete.
 * @param hotjarFilters Other filters (local + the "relevance" outlier) that determine which recordings to delete.
 * @returns (boolean) Whether the Query Deletion has successfully started. Regardless of actual deletion,
 *  the API request guarantees that the matched recordings are immediately hidden (soft-deleted).
 */
export async function sendRecordingsQueryDeletion(
  globalFilters: JsonFilters | null,
  hotjarFilters: HotjarFilters,
): Promise<boolean> {
  if (!globalFilters) {
    return false;
  }

  const payload: QueryDeletionPayload = {
    global_filters: JSON.stringify(
      getFiltersMergedWithRelevanceFilter(globalFilters, hotjarFilters),
    ),
    tz_offset: getUTCOffset(),
  };

  /* The "all" option for the "watched" filter means there is no preference. */
  if (hotjarFilters.watched !== HotjarFilterOptionAll) {
    payload.watched = hotjarFilters.watched === HotjarFilterOptionWatched;
  }

  const response = await axios.post(
    `/api/v1/sites/${getSiteId()}/recordings/query-deletion`,
    payload,
  );
  return response.status === 200;
}
